/*
  custom-design.js — progressive enhancements for the design refresh.
  Defensive by design: every feature feature-detects its target and silently
  no-ops if the markup isn't present, so it can't break any page.
*/
(function () {
  'use strict';

  var reduceMotion =
    window.matchMedia &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  var LX_ICONS = {
    truck:
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M3 7h11v8H3z"/><path d="M14 10h4l3 3v2h-7z"/><circle cx="7" cy="17" r="1.6"/><circle cx="17.5" cy="17" r="1.6"/></svg>',
    shield:
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M12 3l7 3v6c0 4.5-3 7.5-7 9-4-1.5-7-4.5-7-9V6z"/><path d="M9 12l2 2 4-4"/></svg>',
    heart:
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M12 20s-7-4.3-9.2-8.4C1.2 8.6 2.6 5.5 5.6 5.1c1.9-.3 3.4.7 4.4 2 1-1.3 2.5-2.3 4.4-2 3 .4 4.4 3.5 2.8 6.5C19 15.7 12 20 12 20z"/></svg>',
    returns:
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M3 11a8 8 0 1 1 2.3 5.6"/><path d="M3 5v6h6"/></svg>',
    diamond:
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M6 3h12l3 6-9 12L3 9z"/><path d="M3 9h18"/><path d="M9 3 7.4 9 12 21M15 3l1.6 6L12 21"/></svg>',
    lock:
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect x="5" y="11" width="14" height="9" rx="2"/><path d="M8 11V8a4 4 0 0 1 8 0v3"/></svg>',
    drop:
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M12 3s6 6.5 6 11a6 6 0 0 1-12 0c0-4.5 6-11 6-11z"/></svg>',
    star:
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M12 3l2.7 5.9 6.3.7-4.7 4.3 1.3 6.2L12 17.8 6.1 20.4l1.3-6.2L2.7 9.9l6.3-.7z"/></svg>',
  };

  /* Selling taglines shown (in quotes) under every product title. One is picked
     deterministically per product (by handle) so each product keeps a stable
     line, but the copy varies from product to product. Add/edit freely. */
  var LX_TAGLINES = [
    'Add that extra sparkle to any outfit.',
    'The little detail everyone will notice.',
    "Elegance you'll reach for every single day.",
    'Make every day feel a little more special.',
    'Timeless pieces, made to be worn — not saved.',
    'Quietly luxurious. Effortlessly you.',
    'The finishing touch your look deserves.',
    'Turn heads without saying a word.',
    'Crafted to be treasured for years.',
    'A little gold goes a long way.',
    'Because you deserve to shine.',
    'Designed to feel as good as it looks.',
    'Your new everyday signature piece.',
    'Subtle sparkle, serious impact.',
    "The kind of piece you'll never want to take off.",
    'Beautifully made. Endlessly wearable.',
    'Confidence looks good on you.',
    'Elevate the everyday.',
    'Small piece, unforgettable presence.',
    "The compliment magnet you've been missing.",
    'Luxury that fits into real life.',
    'Made to move with you, from day to night.',
    "A gift she'll actually wear every day.",
    'Effortless elegance, every single day.',
    'Polished, refined, unmistakably you.',
    'The detail that makes the whole outfit.',
    'Handcrafted to feel like a real treasure.',
    'Sparkle that goes with absolutely everything.',
    'Quiet luxury you can wear anywhere.',
    'One piece. Endless ways to feel beautiful.',
  ];

  function lxPickTagline() {
    var path = window.location.pathname || '';
    var m = path.match(/\/products\/([^/?#]+)/);
    var key = m ? m[1] : path;
    var h = 0;
    for (var i = 0; i < key.length; i++) {
      h = (h * 31 + key.charCodeAt(i)) >>> 0;
    }
    return LX_TAGLINES[h % LX_TAGLINES.length];
  }

  function lxMoney(cents) {
    var n = Number(cents);
    if (!isFinite(n)) return '';
    return '$' + (n / 100).toFixed(2);
  }

  /* 1. Header elevation once the page is scrolled --------------------------- */
  function initHeaderElevation() {
    var update = function () {
      var scrolled = window.scrollY > 12;
      document.documentElement.classList.toggle('lx-header-scrolled', scrolled);
    };
    update();
    window.addEventListener('scroll', update, { passive: true });
  }

  /* 2. Scroll reveal for top-level sections --------------------------------- */
  function initReveal() {
    if (reduceMotion || !('IntersectionObserver' in window)) return;

    var main = document.querySelector('main');
    if (!main) return;

    var sections = Array.prototype.slice
      .call(main.querySelectorAll(':scope > .shopify-section'))
      .slice(1); // keep the hero/first section instant

    if (!sections.length) return;

    var io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            entry.target.classList.add('lx-in');
            io.unobserve(entry.target);
          }
        });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.05 }
    );

    sections.forEach(function (section) {
      section.classList.add('lx-reveal');
      io.observe(section);
    });
  }

  /* 3. Sticky add-to-cart bar (bottom on mobile, slim top bar on desktop) --- */
  function initStickyAtc() {
    var form = document.querySelector('.product-form__buttons');
    var submit = document.querySelector(
      '.product-form__submit, .product-form__buttons [type="submit"]'
    );
    if (!form || !submit) return;

    // Scope lookups to the buy box so we don't pick up related products,
    // modals or duplicated/visually-hidden nodes.
    var infoContainer =
      submit.closest('.product__info-container') ||
      document.querySelector('.product__info-container') ||
      document;

    var titleEl = infoContainer.querySelector('.product__title');
    var titleText = titleEl
      ? (titleEl.querySelector('h1, h2') || titleEl).textContent.trim()
      : (document.title || '').split(/[–|]/)[0].trim();

    // Effective price only, stripping screen-reader labels like "Regular price".
    var priceWrap = infoContainer.querySelector('.price');
    var onSale = priceWrap && priceWrap.classList.contains('price--on-sale');
    var priceNode =
      (onSale && infoContainer.querySelector('.price-item--sale')) ||
      infoContainer.querySelector('.price__regular .price-item--regular') ||
      infoContainer.querySelector('.price-item--regular');
    var priceText = priceNode
      ? priceNode.textContent.replace(/\s+/g, ' ').trim()
      : '';

    var bar = document.createElement('div');
    bar.className = 'lx-sticky-atc';
    bar.setAttribute('aria-hidden', 'true');

    var info = document.createElement('div');
    info.className = 'lx-sticky-atc__info';

    var title = document.createElement('span');
    title.className = 'lx-sticky-atc__title';
    title.textContent = titleText;

    var price = document.createElement('span');
    price.className = 'lx-sticky-atc__price';
    price.textContent = priceText;

    info.appendChild(title);
    if (price.textContent) info.appendChild(price);

    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'button button--primary lx-sticky-atc__button';
    btn.textContent =
      (submit.querySelector('span') &&
        submit.querySelector('span').textContent.trim()) ||
      submit.textContent.trim() ||
      'Add to cart';

    btn.addEventListener('click', function () {
      submit.click();
    });

    bar.appendChild(info);
    bar.appendChild(btn);
    document.body.appendChild(bar);

    if ('IntersectionObserver' in window) {
      var io = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            // Show the sticky bar only when the real button is out of view
            bar.classList.toggle('lx-in', !entry.isIntersecting);
          });
        },
        { threshold: 0 }
      );
      io.observe(submit);
    }

    // Mirror disabled / sold-out state of the real button
    var sync = function () {
      btn.disabled = submit.disabled;
    };
    sync();
    if ('MutationObserver' in window) {
      new MutationObserver(sync).observe(submit, {
        attributes: true,
        attributeFilter: ['disabled'],
      });
    }
  }

  /* 4. Site-wide USP / trust strip under the header ------------------------- */
  function initUspBar() {
    if (document.querySelector('.lx-usp-bar')) return; // never duplicate

    var headerGroup =
      document.querySelector('.shopify-section-group-header-group') ||
      document.querySelector('header.header') ||
      document.querySelector('.shopify-section--header');
    if (!headerGroup) return;

    var icons = {
      ship:
        '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M3 7h11v8H3z"/><path d="M14 10h4l3 3v2h-7z"/><circle cx="7" cy="17" r="1.6"/><circle cx="17.5" cy="17" r="1.6"/></svg>',
      returns:
        '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M3 12a9 9 0 1 0 3-6.7"/><path d="M3 4v4h4"/></svg>',
      shield:
        '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M12 3l7 3v6c0 4.5-3 7.5-7 9-4-1.5-7-4.5-7-9V6z"/><path d="M9 12l2 2 4-4"/></svg>',
      drop:
        '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M12 3s6 6.5 6 11a6 6 0 0 1-12 0c0-4.5 6-11 6-11z"/></svg>',
    };

    var gift =
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="8" width="18" height="4" rx="1"/><path d="M5 12v9h14v-9"/><path d="M12 8v13"/><path d="M12 8S10.5 4 8.5 4A2.5 2.5 0 0 0 8.5 9H12z"/><path d="M12 8s1.5-4 3.5-4a2.5 2.5 0 0 1 0 5H12z"/></svg>';
    var items = [
      [gift, 'Buy 2, Get 1 FREE' + lxEndsTodayInline(), true],
      [icons.ship, 'Free Shipping Worldwide'],
      [icons.returns, '100-Day Returns'],
      [icons.shield, 'Lifetime Warranty'],
      [icons.drop, 'Waterproof & Tarnish-Free'],
    ];

    var bar = document.createElement('div');
    bar.className = 'lx-usp-bar color-' +
      ((headerGroup.querySelector('[class*="color-"]') &&
        (headerGroup.querySelector('[class*="color-"]').className.match(/color-(\w+)/) || [])[1]) ||
        'scheme-1');
    bar.setAttribute('role', 'complementary');
    bar.setAttribute('aria-label', 'Store guarantees');

    bar.innerHTML = items
      .map(function (it) {
        return (
          '<span class="lx-usp-bar__item' + (it[2] ? ' lx-usp-bar__item--gift' : '') + '">' +
          it[0] + '<span>' + it[1] + '</span></span>'
        );
      })
      .join('');

    headerGroup.insertAdjacentElement('afterend', bar);
  }

  /* 4b. Homepage hero banner promoting the "Buy 2, Get 1 Free" gift offer. --- */
  function initGiftHomeBanner() {
    var isHome =
      document.body.classList.contains('template-index') ||
      /^\/($|\?)/.test(window.location.pathname) ||
      window.location.pathname === '/';
    if (!isHome) return;
    if (document.querySelector('.lx-gift-banner')) return;
    var main = document.querySelector('#MainContent') || document.querySelector('main');
    if (!main) return;

    var gift =
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="8" width="18" height="4" rx="1"/><path d="M5 12v9h14v-9"/><path d="M12 8v13"/><path d="M12 8S10.5 4 8.5 4A2.5 2.5 0 0 0 8.5 9H12z"/><path d="M12 8s1.5-4 3.5-4a2.5 2.5 0 0 1 0 5H12z"/></svg>';
    var a = document.createElement('a');
    a.className = 'lx-gift-banner';
    a.href = '/collections/bestseller';
    a.setAttribute('aria-label', 'Buy 2, get 1 free — shop bestsellers');
    a.innerHTML =
      '<span class="lx-gift-banner__title">Buy 2, Get 1 Free \u2014 Final Hours</span>' +
      '<div class="lx-gift-banner__timer" aria-live="polite">' +
      '<span class="lx-gift-banner__seg"><span class="lx-gift-banner__num" data-lx-cd="h">--</span><span class="lx-gift-banner__unit">Hours</span></span>' +
      '<span class="lx-gift-banner__colon">:</span>' +
      '<span class="lx-gift-banner__seg"><span class="lx-gift-banner__num" data-lx-cd="m">--</span><span class="lx-gift-banner__unit">Mins</span></span>' +
      '<span class="lx-gift-banner__colon">:</span>' +
      '<span class="lx-gift-banner__seg"><span class="lx-gift-banner__num" data-lx-cd="s">--</span><span class="lx-gift-banner__unit">Sec</span></span>' +
      '</div>' +
      '<span class="lx-gift-banner__cta">Shop Now</span>';
    // Place the banner directly beneath the hero (title image), flush with no gap.
    var firstSection = main.querySelector(':scope > .shopify-section');
    if (firstSection) firstSection.insertAdjacentElement('afterend', a);
    else main.insertBefore(a, main.firstChild);
  }

  /* 4c. Tidy the header stack: drop empty header-group spacer sections that
     leave a blank white strip above the content. Idempotent + defensive;
     never touches the header itself or the announcement bar. */
  function initTidyHeader() {
    var secs = document.querySelectorAll('body > .shopify-section-group-header-group');
    Array.prototype.forEach.call(secs, function (s) {
      var id = s.id || '';
      if (/__header$/.test(id) || /__announcement-bar$/.test(id)) return;
      if (s.getAttribute('data-lx-hidden')) return;
      var hasText = (s.textContent || '').trim().length > 0;
      var hasMedia = s.querySelector('img, video, svg, picture, iframe, a[href], button');
      if (!hasText && !hasMedia) {
        s.style.display = 'none';
        s.setAttribute('data-lx-hidden', '1');
      }
    });
  }

  /* 5. Volume-discount offer: live buy-box widget + cart-drawer progress ---- */
  var LX_TIERS = [
    { q: 2, pct: 25 },
    { q: 3, pct: 35 },
    { q: 4, pct: 45 },
  ];

  function lxTierState(qty) {
    var unlocked = null;
    var next = null;
    for (var i = 0; i < LX_TIERS.length; i++) {
      if (qty >= LX_TIERS[i].q) unlocked = LX_TIERS[i];
      else {
        next = LX_TIERS[i];
        break;
      }
    }
    return { unlocked: unlocked, next: next, need: next ? next.q - qty : 0 };
  }

  function lxOfferMessage(qty) {
    if (qty >= 2) {
      return '<strong>Free gift unlocked.</strong> Your cheapest piece is <strong>free</strong>';
    }
    if (qty === 1) {
      return 'Add <strong>1 more piece</strong> and your 3rd is <strong>free</strong>';
    }
    return 'Add <strong>2 pieces</strong> and your 3rd piece is <strong>free</strong>';
  }

  function lxDebounce(fn, wait) {
    var t;
    return function () {
      clearTimeout(t);
      t = setTimeout(fn, wait);
    };
  }

  var lxCountdownTimer = null;

  function lxMsUntilMidnight() {
    var now = new Date();
    var end = new Date(now);
    end.setHours(24, 0, 0, 0);
    return end - now;
  }

  function lxCountdownParts() {
    var ms = lxMsUntilMidnight();
    if (ms < 0) ms = 0;
    var total = Math.floor(ms / 1000);
    var h = Math.floor(total / 3600);
    var m = Math.floor((total % 3600) / 60);
    var s = total % 60;
    return {
      h: String(h).padStart(2, '0'),
      m: String(m).padStart(2, '0'),
      s: String(s).padStart(2, '0'),
    };
  }

  function lxCountdownText() {
    var ms = lxMsUntilMidnight();
    if (ms <= 0) return 'Ending soon';
    var p = lxCountdownParts();
    return parseInt(p.h, 10) + 'h ' + p.m + 'm ' + p.s + 's';
  }

  function lxCountdownBlock(label) {
    return (
      '<div class="lx-timerbar" aria-live="polite">' +
      '<span class="lx-timerbar__label">' +
      (label || 'Ends tonight at midnight') +
      '</span>' +
      '<span class="lx-timerbar__clock">' +
      '<span class="lx-timerbar__seg"><b data-lx-cd="h">--</b><i>Hours</i></span>' +
      '<span class="lx-timerbar__colon">:</span>' +
      '<span class="lx-timerbar__seg"><b data-lx-cd="m">--</b><i>Mins</i></span>' +
      '<span class="lx-timerbar__colon">:</span>' +
      '<span class="lx-timerbar__seg"><b data-lx-cd="s">--</b><i>Sec</i></span>' +
      '</span>' +
      '</div>'
    );
  }

  function lxEndsTodayPrefix() {
    return '\u23F0 Ends today \u2014 ';
  }

  function lxEndsTodayInline() {
    return ' <span class="lx-urgency-pill">Ends today</span>';
  }

  function lxRefreshCountdowns() {
    var text = lxCountdownText();
    document.querySelectorAll('[data-lx-countdown]').forEach(function (el) {
      el.textContent = text;
    });
    var parts = lxCountdownParts();
    document.querySelectorAll('[data-lx-cd]').forEach(function (el) {
      var unit = el.getAttribute('data-lx-cd');
      if (parts[unit] != null && el.textContent !== parts[unit]) {
        el.textContent = parts[unit];
      }
    });
  }

  function initOfferCountdown() {
    lxRefreshCountdowns();
    if (lxCountdownTimer) return;
    lxCountdownTimer = setInterval(lxRefreshCountdowns, 1000);
  }

  // Site-wide announcement-bar copy that moves with the cart.
  function lxOfferBanner(qty) {
    if (qty <= 0) {
      return lxEndsTodayPrefix() + 'Buy 2, get 1 free \u2014 add any 2 pieces and your 3rd is on us';
    }
    if (qty === 1) {
      return lxEndsTodayPrefix() + 'Almost there \u2014 add 1 more piece and your 3rd is free';
    }
    return 'Free gift unlocked \u2014 your cheapest piece is free at checkout';
  }

  function renderOfferBanner(qty) {
    var span = document.querySelector('.announcement-bar__message span');
    if (!span) return;
    var msg = lxOfferBanner(qty);
    if (span.textContent !== msg) span.textContent = msg;
    var bar = span.closest('.announcement-bar');
    if (bar) bar.classList.toggle('lx-offer-unlocked', qty >= 2);
  }

  function renderBundleWidget(qty) {
    var root = document.querySelector('[data-lx-bundle]');
    if (!root) return;
    var s = lxTierState(qty);
    Array.prototype.forEach.call(
      root.querySelectorAll('.lx-bundle__tier'),
      function (t) {
        var tq = parseInt(t.getAttribute('data-qty'), 10);
        t.classList.toggle('is-active', qty >= tq);
        t.classList.toggle('is-next', !!(s.next && s.next.q === tq));
      }
    );
    var hint = root.querySelector('[data-lx-bundle-hint]');
    if (hint) hint.innerHTML = lxOfferMessage(qty);
  }

  // Gift-offer copy for the upsell carousel so the slider actively sells the
  // "buy 2, get 1 free" idea instead of a static headline.
  // Progressive gift copy for the upsell carousel. The BXGY repeats (every 3rd
  // eligible piece is free), so once a gift is already picked we stop telling
  // the customer to "pick a free gift" and instead upsell the next tier:
  // "add 2 more pieces -> unlock another free gift".
  function lxUpsellCopy(qty) {
    qty = qty || 0;
    var freebies = Math.floor(qty / 3); // free pieces already earned
    var toNext = (((2 - qty) % 3) + 3) % 3; // pieces until the next free is claimable

    if (qty < 2) {
      return {
        title: '\uD83C\uDF81 Buy 2, Get 1 <b>FREE</b> <span class="lx-urgency-pill">Ends today</span>',
        sub:
          (qty === 1 ? 'Add 1 more piece' : 'Add any 2 pieces') +
          ' and your 3rd piece is on us \u2014 offer ends tonight at midnight.',
      };
    }
    if (toNext === 0) {
      // a free piece is claimable right now (cart of 2, 5, 8, …)
      return {
        title:
          freebies >= 1
            ? '\uD83C\uDF89 <b>Another</b> free gift unlocked!'
            : '\uD83C\uDF89 You\u2019ve unlocked a FREE gift',
        sub: 'Add one more piece below \u2014 your cheapest extra piece is free at checkout.',
      };
    }
    // gift(s) already secured — nudge toward the next free piece
    return {
      title: '\uD83C\uDF81 Add ' + toNext + ' more \u2192 <b>another</b> free gift',
      sub:
        'You\u2019ve already unlocked ' +
        freebies +
        ' free ' +
        (freebies === 1 ? 'piece' : 'pieces') +
        '. Every 3rd piece is on us \u2014 keep the deal going!',
    };
  }

  function renderUpsellHeading(qty) {
    var t = document.querySelector('.lx-upsell__title');
    var sub = document.querySelector('.lx-upsell__sub');
    if (!t && !sub) return;
    var c = lxUpsellCopy(qty);
    if (t) t.innerHTML = c.title;
    if (sub) sub.textContent = c.sub;
  }

  // Catch every cart mutation no matter how the theme/app performs it, by
  // wrapping fetch + XHR for the Shopify cart AJAX endpoints. This is far more
  // reliable than watching DOM nodes that vary between themes and apps.
  var lxCartHookInstalled = false;

  function hookCartRequests(onChange) {
    if (lxCartHookInstalled) return;
    lxCartHookInstalled = true;
    var rx = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/i;

    if (window.fetch) {
      var origFetch = window.fetch;
      window.fetch = function () {
        var input = arguments[0];
        var url =
          typeof input === 'string'
            ? input
            : (input && input.url) || '';
        var p = origFetch.apply(this, arguments);
        if (rx.test(url)) {
          p.then(function () {
            onChange();
          }).catch(function () {});
        }
        return p;
      };
    }

    if (window.XMLHttpRequest) {
      var proto = XMLHttpRequest.prototype;
      var origOpen = proto.open;
      var origSend = proto.send;
      proto.open = function (method, url) {
        this.__lxCart = rx.test(url || '');
        return origOpen.apply(this, arguments);
      };
      proto.send = function () {
        if (this.__lxCart) {
          this.addEventListener('loadend', function () {
            onChange();
          });
        }
        return origSend.apply(this, arguments);
      };
    }
  }

  /* ---------------------------------------------------------------------------
     Snapchat Pixel — ADD_CART event. The base pixel (loader + PAGE_VIEW +
     product VIEW_CONTENT) lives in snippets/snapchat-pixel.liquid and only
     loads once a Pixel ID is set in Theme settings. Here we fire ADD_CART from
     the /cart/add.js response so it covers every add path (PDP, gift box,
     cart-drawer upsell, quick-add). No-ops entirely until snaptr is present.
  --------------------------------------------------------------------------- */
  function lxSnapReady() {
    return typeof window !== 'undefined' && typeof window.snaptr === 'function';
  }

  function lxSnapCurrency() {
    if (window.__lxSnapCurrency) return window.__lxSnapCurrency;
    try {
      if (window.Shopify && Shopify.currency && Shopify.currency.active) return Shopify.currency.active;
    } catch (e) {}
    return 'USD';
  }

  function lxSnapTrackAdd(data) {
    if (!lxSnapReady() || !data) return;
    // /cart/add.js returns a single line item, or { items: [...] } for bundles.
    var items = data.items || (data.id ? [data] : []);
    if (!items.length) return;
    var ids = [];
    var qty = 0;
    var priceCents = 0;
    var category = '';
    for (var i = 0; i < items.length; i++) {
      var it = items[i] || {};
      var id = it.id != null ? it.id : it.variant_id != null ? it.variant_id : it.product_id;
      if (id != null) ids.push(String(id));
      var q = it.quantity || 1;
      qty += q;
      var line = it.final_line_price != null ? it.final_line_price : it.line_price != null ? it.line_price : (it.price || 0) * q;
      priceCents += line;
      if (!category && it.product_type) category = it.product_type;
    }
    var payload = {
      price: Math.round(priceCents) / 100,
      currency: lxSnapCurrency(),
      item_ids: ids,
      number_items: qty,
    };
    if (category) payload.item_category = category;
    try {
      window.snaptr('track', 'ADD_CART', payload);
    } catch (e) {}
  }

  function initSnapTracking() {
    if (window.__lxSnapAddHook) return;
    window.__lxSnapAddHook = true;
    var rx = /\/cart\/add(\.js)?(\?|$)/i;

    if (window.fetch) {
      var origFetch = window.fetch;
      window.fetch = function () {
        var input = arguments[0];
        var url = typeof input === 'string' ? input : (input && input.url) || '';
        var p = origFetch.apply(this, arguments);
        if (rx.test(url)) {
          p.then(function (res) {
            try {
              res
                .clone()
                .json()
                .then(lxSnapTrackAdd)
                .catch(function () {});
            } catch (e) {}
          }).catch(function () {});
        }
        return p;
      };
    }

    if (window.XMLHttpRequest) {
      var proto = XMLHttpRequest.prototype;
      var origOpen = proto.open;
      var origSend = proto.send;
      proto.open = function (method, url) {
        this.__lxSnapAdd = rx.test(url || '');
        return origOpen.apply(this, arguments);
      };
      proto.send = function () {
        if (this.__lxSnapAdd) {
          var xhr = this;
          xhr.addEventListener('loadend', function () {
            try {
              lxSnapTrackAdd(JSON.parse(xhr.responseText));
            } catch (e) {}
          });
        }
        return origSend.apply(this, arguments);
      };
    }
  }

  /* Keep drawer, cart page and header bubble in sync after ANY cart mutation.
     Custom /cart/add.js calls (upsell, bundle box) skip Dawn's section render,
     so the drawer stayed stale until a full page reload. */
  var lxCartSyncPending = false;

  function lxBroadcastCartChange(opts) {
    opts = opts || {};
    if (lxCartSyncPending) return Promise.resolve();
    lxCartSyncPending = true;

    var cartUrl = (window.routes && window.routes.cart_url) || '/cart';
    var jobs = [];

    jobs.push(
      fetch('/cart.js', {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
        cache: 'no-store',
      })
        .then(function (r) {
          return r.json();
        })
        .then(function (cart) {
          var count = cart && typeof cart.item_count === 'number' ? cart.item_count : 0;
          document.querySelectorAll('.cart-count-bubble').forEach(function (b) {
            var span =
              b.querySelector('span[aria-hidden="true"]') ||
              b.querySelector('span') ||
              b;
            if (span) span.textContent = count;
          });
          var drawer = document.querySelector('cart-drawer');
          if (drawer && count > 0) {
            drawer.classList.remove('is-empty');
            var inner = drawer.querySelector('.drawer__inner');
            if (inner) inner.classList.remove('is-empty');
          }
          // Keep the offer surfaces (announcement bar, PDP bundle widget, upsell
          // heading) in sync from the single central cart path. This replaces the
          // MutationObserver-based refresh in initOffers(), which fed back on its
          // own drawer re-renders and flooded /cart.js on every add.
          try {
            renderOfferBanner(count);
            renderBundleWidget(count);
            renderUpsellHeading(count);
          } catch (e) {}
        })
        .catch(function () {})
    );

    if (document.querySelector('cart-drawer')) {
      jobs.push(
        fetch(cartUrl + '?section_id=cart-drawer&_=' + Date.now(), {
          credentials: 'same-origin',
          cache: 'no-store',
        })
          .then(function (r) {
            return r.text();
          })
          .then(function (html) {
            var doc = new DOMParser().parseFromString(html, 'text/html');
            var cd = document.getElementById('CartDrawer');
            var fresh = doc.getElementById('CartDrawer');
            if (cd && fresh) cd.innerHTML = fresh.innerHTML;
            var drawer = document.querySelector('cart-drawer');
            if (drawer) {
              drawer.classList.remove('is-empty');
              var overlay = drawer.querySelector('#CartDrawer-Overlay');
              if (overlay) overlay.addEventListener('click', drawer.close.bind(drawer));
            }
          })
          .catch(function () {})
      );
    }

    var cartPage = document.querySelector('cart-items');
    if (cartPage && typeof cartPage.onCartUpdate === 'function') {
      jobs.push(cartPage.onCartUpdate());
    }

    return Promise.all(jobs)
      .then(function () {
        if (
          typeof publish === 'function' &&
          window.PUB_SUB_EVENTS &&
          window.PUB_SUB_EVENTS.cartUpdate
        ) {
          publish(window.PUB_SUB_EVENTS.cartUpdate, { source: 'lx-custom-design' });
        }
        if (opts.open) {
          var drawer = document.querySelector('cart-drawer');
          if (drawer && typeof drawer.open === 'function') drawer.open();
        }
        lxRefreshCountdowns();
      })
      .finally(function () {
        lxCartSyncPending = false;
      });
  }

  function lxInitCartSync() {
    var debounced = lxDebounce(function () {
      lxBroadcastCartChange({});
    }, 250);
    hookCartRequests(debounced);
    window.addEventListener('pageshow', debounced);
  }

  // Pulsing in-stock / ready-to-ship reassurance right above the CTA.
  function initBuyboxEnhancements() {
    var info = document.querySelector('.product__info-container');
    if (!info) return;
    initPdpExpressRow(info);

    var buttons = info.querySelector('.product-form__buttons');
    if (!info.querySelector('.lx-stock')) {
      var stock = document.createElement('div');
      stock.className = 'lx-stock';
      stock.innerHTML =
        '<span class="lx-stock__dot" aria-hidden="true"></span>' +
        '<span><strong>In stock</strong> · ships in 24h</span>';
      if (buttons && buttons.parentNode) {
        buttons.parentNode.insertBefore(stock, buttons);
      } else {
        info.insertBefore(stock, info.firstChild);
      }
    }

    // Slim trust strip under the CTA (short labels only, no long body copy).
    // Uniform 38x24 badges: same size, same radius, path logos (no SVG text).
    var payIcons = [
      '<svg class="lx-pay-ico" viewBox="0 0 38 24" role="img" aria-label="Shop Pay"><rect width="38" height="24" rx="3" fill="#5A31F4"/><text x="19" y="15.5" text-anchor="middle" fill="#fff" font-family="Arial,Helvetica,sans-serif" font-size="8.5" font-weight="700" letter-spacing="0.2">Shop Pay</text></svg>',
      '<svg class="lx-pay-ico" viewBox="0 0 38 24" role="img" aria-label="Apple Pay"><rect width="38" height="24" rx="3" fill="#000"/><path fill="#fff" d="M11.35 8.05c-.42.5-1.1.88-1.72.83-.08-.64.24-1.32.6-1.75.42-.48 1.14-.84 1.73-.86.07.68-.2 1.33-.61 1.78zm.62 1c-.9-.05-1.67.52-2.1.52-.44 0-1.1-.49-1.82-.48-.93.01-1.8.55-2.27 1.4-.97 1.7-.25 4.22.7 5.6.47.68 1.02 1.44 1.74 1.41.7-.03.96-.45 1.8-.45s1.07.45 1.8.44c.75-.01 1.22-.69 1.68-1.37.53-.78.75-1.55.76-1.58-.01 0-1.46-.56-1.47-2.23-.01-1.4 1.13-2.08 1.19-2.11-.65-.96-1.66-1.07-2.01-1.09z"/><text x="24.2" y="15.6" fill="#fff" font-family="Arial,Helvetica,sans-serif" font-size="9.5" font-weight="500">Pay</text></svg>',
      '<svg class="lx-pay-ico" viewBox="0 0 38 24" role="img" aria-label="Google Pay"><rect x=".5" y=".5" width="37" height="23" rx="2.5" fill="#fff" stroke="#E0E0E0"/><text x="19" y="15.5" text-anchor="middle" font-family="Arial,Helvetica,sans-serif" font-size="9" font-weight="600"><tspan fill="#4285F4">G</tspan><tspan fill="#5F6368"> Pay</tspan></text></svg>',
      '<svg class="lx-pay-ico" viewBox="0 0 38 24" role="img" aria-label="PayPal"><rect x=".5" y=".5" width="37" height="23" rx="2.5" fill="#fff" stroke="#E0E0E0"/><text x="19" y="15.5" text-anchor="middle" font-family="Arial,Helvetica,sans-serif" font-size="9" font-weight="700" font-style="italic"><tspan fill="#003087">Pay</tspan><tspan fill="#009CDE">Pal</tspan></text></svg>',
      '<svg class="lx-pay-ico" viewBox="0 0 38 24" role="img" aria-label="Visa"><rect x=".5" y=".5" width="37" height="23" rx="2.5" fill="#fff" stroke="#E0E0E0"/><text x="19" y="15.8" text-anchor="middle" fill="#1A1F71" font-family="Arial,Helvetica,sans-serif" font-size="10" font-weight="700" font-style="italic" letter-spacing="1">VISA</text></svg>',
      '<svg class="lx-pay-ico" viewBox="0 0 38 24" role="img" aria-label="Mastercard"><rect x=".5" y=".5" width="37" height="23" rx="2.5" fill="#fff" stroke="#E0E0E0"/><circle cx="15.2" cy="12" r="5.4" fill="#EB001B"/><circle cx="22.8" cy="12" r="5.4" fill="#F79E1B"/><path d="M19 7.8a5.4 5.4 0 0 0 0 8.4 5.4 5.4 0 0 0 0-8.4z" fill="#FF5F00"/></svg>',
      '<svg class="lx-pay-ico" viewBox="0 0 38 24" role="img" aria-label="American Express"><rect width="38" height="24" rx="3" fill="#016FD0"/><text x="19" y="15.6" text-anchor="middle" fill="#fff" font-family="Arial,Helvetica,sans-serif" font-size="8" font-weight="700" letter-spacing="0.6">AMEX</text></svg>'
    ].join('');
    var slimHtml =
      '<div class="lx-guarantee__main">' +
      '<span class="lx-guarantee__seal" aria-hidden="true">' +
      LX_ICONS.shield +
      '</span>' +
      '<span class="lx-guarantee__text"><strong>100-day money-back</strong></span>' +
      '</div>' +
      '<ul class="lx-guarantee__row">' +
      '<li>' + LX_ICONS.truck + '<span>Ships in 24h</span></li>' +
      '<li>' + LX_ICONS.returns + '<span>Free returns</span></li>' +
      '<li>' + LX_ICONS.lock + '<span>Secure pay</span></li>' +
      '</ul>' +
      '<div class="lx-guarantee__pay" role="group" aria-label="Accepted payment methods">' +
      '<span class="lx-guarantee__pay-icons">' +
      payIcons +
      '</span>' +
      '</div>';
    var box = info.querySelector('.lx-guarantee');
    if (!box) {
      box = document.createElement('div');
      box.className = 'lx-guarantee';
      if (buttons && buttons.parentNode) {
        buttons.insertAdjacentElement('afterend', box);
      } else {
        info.appendChild(box);
      }
    }
    box.innerHTML = slimHtml;
  }

  /* PDP: ADD TO CART, then Shop Pay | Apple Pay side-by-side.
     Shopify only recommends ONE wallet on product pages. Shop Pay stays as the
     native recommended button; when Apple Pay is available on this device we
     mount a second shopify-accelerated-checkout host with recommended=apple_pay
     so the Apple Pay button is a real wallet (not "More payment options"). */
  function lxCanUseApplePay() {
    try {
      return !!(
        window.ApplePaySession &&
        typeof window.ApplePaySession.canMakePayments === 'function' &&
        window.ApplePaySession.canMakePayments()
      );
    } catch (err) {
      return false;
    }
  }

  function lxApplePayWalletParams() {
    var node = document.getElementById('apple-pay-shop-capabilities');
    if (!node || !node.textContent) return null;
    try {
      var caps = JSON.parse(node.textContent);
      if (!caps || !caps.merchantName) return null;
      return {
        shopId: caps.shopId,
        countryCode: caps.countryCode,
        currencyCode: caps.currencyCode,
        merchantCapabilities: caps.merchantCapabilities,
        merchantId: caps.merchantId,
        merchantName: caps.merchantName,
        requiredBillingContactFields: caps.requiredBillingContactFields,
        requiredShippingContactFields: caps.requiredShippingContactFields,
        shippingType: caps.shippingType,
        supportedNetworks: caps.supportedNetworks,
        shopifyPaymentsEnabled: caps.shopifyPaymentsEnabled,
        supportsSubscriptions: caps.supportsSubscriptions,
      };
    } catch (err) {
      return null;
    }
  }

  function lxMountApplePayHost(accel) {
    var params = lxApplePayWalletParams();
    if (!params || !accel) return null;

    var host = document.createElement('div');
    host.className = 'shopify-payment-button lx-pdp-express__apple-host';
    host.setAttribute('data-shopify', 'payment-button');

    var appleAccel = document.createElement('shopify-accelerated-checkout');
    Array.prototype.forEach.call(accel.attributes, function (attr) {
      if (attr.name === 'recommended') return;
      appleAccel.setAttribute(attr.name, attr.value);
    });
    appleAccel.setAttribute(
      'recommended',
      JSON.stringify({
        supports_subs: true,
        supports_def_opts: false,
        name: 'apple_pay',
        wallet_params: params,
      })
    );
    host.appendChild(appleAccel);
    return host;
  }

  function initPdpExpressRow(root) {
    var scope = root || document;
    var buttons = scope.querySelector('.product-form__buttons');
    if (!buttons || buttons.__lxPdpExpress) return;
    var pay = buttons.querySelector('.shopify-payment-button');
    var accel = buttons.querySelector('shopify-accelerated-checkout');
    if (!pay || !accel) {
      // Wallet host often mounts a beat later; retry a few times.
      buttons.__lxPdpExpressRetries = (buttons.__lxPdpExpressRetries || 0) + 1;
      if (buttons.__lxPdpExpressRetries < 8) {
        setTimeout(function () {
          initPdpExpressRow(scope);
        }, 400);
      }
      return;
    }
    buttons.__lxPdpExpress = true;

    var row = document.createElement('div');
    row.className = 'lx-pdp-express';

    var shopWrap = document.createElement('div');
    shopWrap.className = 'lx-pdp-express__shop';
    pay.parentNode.insertBefore(row, pay);
    shopWrap.appendChild(pay);
    row.appendChild(shopWrap);

    var appleWrap = document.createElement('div');
    appleWrap.className = 'lx-pdp-express__apple';

    var mountApple = function () {
      if (!lxCanUseApplePay()) {
        row.classList.add('lx-pdp-express--shop-only');
        appleWrap.hidden = true;
        return;
      }
      var appleHost = lxMountApplePayHost(accel);
      if (!appleHost) {
        row.classList.add('lx-pdp-express--shop-only');
        appleWrap.hidden = true;
        return;
      }
      appleWrap.appendChild(appleHost);
      // If Shopify decides Apple Pay is ineligible, fall back to Shop Pay only.
      setTimeout(function () {
        var realBtn =
          appleWrap.querySelector('shopify-apple-pay-button') ||
          appleWrap.querySelector('.apple-pay-button');
        var skeleton = appleWrap.querySelector(
          '.shopify-payment-button__skeleton'
        );
        if (!realBtn && !skeleton) {
          row.classList.add('lx-pdp-express--shop-only');
          appleWrap.hidden = true;
        }
      }, 2500);
    };

    row.appendChild(appleWrap);

    if (window.customElements && customElements.whenDefined) {
      customElements
        .whenDefined('shopify-accelerated-checkout')
        .then(mountApple)
        .catch(mountApple);
    } else {
      mountApple();
    }
  }

  // Custom upsell carousel placed directly under the product. One-tap add
  // feeds the cart hook, which instantly updates the offer bar + buy-box hint.
  function initUpsellSlider() {
    var info = document.querySelector('.product__info-container');
    var productEl = document.querySelector('.product');
    if (!info || !productEl) return;

    var anchor = productEl.closest('.shopify-section') || productEl;

    var recEl = document.querySelector('product-recommendations');
    var pid =
      (recEl && recEl.dataset && recEl.dataset.productId) ||
      (window.ShopifyAnalytics &&
        window.ShopifyAnalytics.meta &&
        window.ShopifyAnalytics.meta.product &&
        window.ShopifyAnalytics.meta.product.id);
    if (!pid) return;

    // Replace the native related-products section to avoid duplicate sliders.
    if (recEl) {
      var nativeSection = recEl.closest('.shopify-section');
      if (nativeSection && nativeSection !== anchor) {
        nativeSection.style.display = 'none';
      }
    }

    if (document.querySelector('.lx-upsell')) return;

    var wrap = document.createElement('section');
    wrap.className = 'lx-upsell';
    wrap.innerHTML =
      '<div class="lx-upsell__head">' +
      '<h2 class="lx-upsell__title">\uD83C\uDF81 Buy 2, Get 1 FREE <span class="lx-urgency-pill">Ends today</span></h2>' +
      lxCountdownBlock('Offer ends tonight') +
      '</div>' +
      '<div class="lx-upsell__track" data-lx-upsell-track></div>';
    anchor.insertAdjacentElement('afterend', wrap);
    var track = wrap.querySelector('[data-lx-upsell-track]');

    fetch(
      '/recommendations/products.json?product_id=' +
        encodeURIComponent(pid) +
        '&limit=10&intent=related',
      { headers: { Accept: 'application/json' } }
    )
      .then(function (r) {
        return r.json();
      })
      .then(function (d) {
        var products = (d && d.products) || [];
        if (!products.length) {
          wrap.remove();
          return;
        }
        products.forEach(function (p) {
          var variants = p.variants || [];
          var v =
            variants.filter(function (x) {
              return x.available;
            })[0] || variants[0];
          if (!v) return;
          var img =
            p.featured_image ||
            (p.images && p.images[0]) ||
            (v && v.featured_image && v.featured_image.src) ||
            '';
          var title = (p.title || '').replace(/"/g, '&quot;');
          var card = document.createElement('div');
          card.className = 'lx-upsell__card';
          card.innerHTML =
            '<a class="lx-upsell__media" href="' +
            p.url +
            '" tabindex="-1">' +
            (img
              ? '<img loading="lazy" src="' + img + '" alt="' + title + '">'
              : '') +
            '</a>' +
            '<a class="lx-upsell__name" href="' +
            p.url +
            '">' +
            (p.title || '') +
            '</a>' +
            '<div class="lx-upsell__price">' +
            lxMoney(v.price) +
            '</div>' +
            '<button type="button" class="button lx-upsell__add" data-vid="' +
            v.id +
            '">Add</button>';
          track.appendChild(card);
        });

        // Final "see more" card linking to the bestseller collection.
        var more = document.createElement('a');
        more.className = 'lx-upsell__card lx-upsell__more';
        more.href = '/collections/bestseller';
        more.innerHTML =
          '<span class="lx-upsell__more-inner">' +
          '<span class="lx-upsell__more-icon" aria-hidden="true">&rarr;</span>' +
          '<span class="lx-upsell__more-title">View all bestsellers</span>' +
          '<span class="lx-upsell__more-sub">Discover more pieces &amp; keep saving</span>' +
          '</span>';
        track.appendChild(more);

        track.addEventListener('click', function (e) {
          var btn = e.target.closest('.lx-upsell__add');
          if (!btn || btn.disabled) return;
          var vid = btn.getAttribute('data-vid');
          var old = btn.textContent;
          btn.disabled = true;
          btn.textContent = 'Adding\u2026';
          fetch('/cart/add.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json',
            },
            body: JSON.stringify({ items: [{ id: vid, quantity: 1 }] }),
          })
            .then(function (r) {
              return r.json();
            })
            .then(function () {
              btn.textContent = 'Added \u2713';
              btn.classList.add('is-added');
              lxBroadcastCartChange({ open: true });
              setTimeout(function () {
                btn.textContent = old;
                btn.disabled = false;
                btn.classList.remove('is-added');
              }, 1600);
            })
            .catch(function () {
              btn.textContent = old;
              btn.disabled = false;
            });
        });
      })
      .catch(function () {
        wrap.remove();
      });
  }

  function initOffers() {
    // The cart drawer already has a tiered-discount progress bar from the
    // discount app, so we don't duplicate it. Here we keep the site-wide
    // announcement bar and the product buy-box hint in sync with the cart.
    var hasBundle = document.querySelector('[data-lx-bundle]');
    var hasBanner = document.querySelector('.announcement-bar__message span');
    if (!hasBundle && !hasBanner) return;

    var pending = false;
    function refresh() {
      if (pending) return;
      pending = true;
      fetch('/cart.js', {
        headers: { Accept: 'application/json' },
        cache: 'no-store',
      })
        .then(function (r) {
          return r.json();
        })
        .then(function (cart) {
          pending = false;
          var qty =
            cart && typeof cart.item_count === 'number' ? cart.item_count : 0;
          renderOfferBanner(qty);
          renderBundleWidget(qty);
          renderUpsellHeading(qty);
        })
        .catch(function () {
          pending = false;
        });
    }

    var debounced = lxDebounce(refresh, 150);

    // Cart mutations are handled centrally by lxInitCartSync() ->
    // lxBroadcastCartChange(), which now also refreshes these offer surfaces.
    // We intentionally do NOT observe the cart drawer / cart bubble here: doing so
    // created a feedback loop (drawer re-render -> observer -> refresh ->
    // re-render) that flooded /cart.js and could leave the drawer showing a stale
    // state after adding an upsell item.
    window.addEventListener('pageshow', debounced);

    refresh();
  }




/*__LX_FLOATBG_START__*/
  /* PHASE 14 — store-wide drifting jewelry (mobile: 9 pieces visible) */
  function initFloatBg() {
    var main = document.getElementById('MainContent');
    if (!main) return;
    if (main.querySelector('.lx-floatbg')) return;
    var path = (window.location.pathname || '');
    var isProduct = /^\/products\//.test(path) || !!main.querySelector('.lx-bundle');
    var isHome = path === '/' || path === '';
    var isCollection = /^\/collections\//.test(path);
    var isOther = /^\/(pages|search|blogs)(\/|$)/.test(path);
    if (!(isProduct || isHome || isCollection || isOther)) return;
    var full = isProduct || isHome;
    var calm = !full;
    var all = [{"src":"https://cdn.shopify.com/s/files/1/0999/1736/5533/files/cut-05-dragonfly-sm.png?v=1782369526","top":"2%","left":"8%","w":195,"op":0.6,"path":"lxDriftA","dur":"30s","delay":"0s","spin":"60s","blur":0,"core":true,"hideSm":false},{"src":"https://cdn.shopify.com/s/files/1/0999/1736/5533/files/cut-10-crown-ring-sm.png?v=1782372183","top":"8%","left":"60%","w":150,"op":0.56,"path":"lxDriftB","dur":"26s","delay":"2s","spin":"54s","blur":0,"core":false,"hideSm":true},{"src":"https://cdn.shopify.com/s/files/1/0999/1736/5533/files/cut-01-aqua-hoops-sm.png?v=1782369492","top":"15%","left":"34%","w":145,"op":0.62,"path":"lxDriftC","dur":"34s","delay":"1s","spin":"70s","blur":0,"core":true,"hideSm":false},{"src":"https://cdn.shopify.com/s/files/1/0999/1736/5533/files/cut-11-owl-ring-sm.png?v=1782372193","top":"22%","left":"80%","w":135,"op":0.56,"path":"lxDriftD","dur":"22s","delay":"3s","spin":"48s","blur":0,"core":false,"hideSm":true},{"src":"https://cdn.shopify.com/s/files/1/0999/1736/5533/files/cut-06-daisy-sm.png?v=1782369535","top":"30%","left":"14%","w":125,"op":0.58,"path":"lxDriftE","dur":"36s","delay":"1.5s","spin":"66s","blur":0,"core":true,"hideSm":false},{"src":"https://cdn.shopify.com/s/files/1/0999/1736/5533/files/cut-14-triangle-earrings-sm.png?v=1782372222","top":"37%","left":"52%","w":140,"op":0.54,"path":"lxDriftF","dur":"28s","delay":"4s","spin":"58s","blur":1,"core":false,"hideSm":true},{"src":"https://cdn.shopify.com/s/files/1/0999/1736/5533/files/cut-13-solitaire-ring-sm.png?v=1782372213","top":"44%","left":"30%","w":110,"op":0.6,"path":"lxDriftB","dur":"32s","delay":"2.5s","spin":"62s","blur":0,"core":true,"hideSm":false},{"src":"https://cdn.shopify.com/s/files/1/0999/1736/5533/files/cut-09-gold-hoops-sm.png?v=1782369556","top":"51%","left":"72%","w":130,"op":0.56,"path":"lxDriftA","dur":"24s","delay":"1s","spin":"50s","blur":0,"core":false,"hideSm":false},{"src":"https://cdn.shopify.com/s/files/1/0999/1736/5533/files/cut-07-wave-cuff-sm.png?v=1782369543","top":"58%","left":"10%","w":165,"op":0.52,"path":"lxDriftD","dur":"38s","delay":"3.5s","spin":"72s","blur":1,"core":true,"hideSm":false},{"src":"https://cdn.shopify.com/s/files/1/0999/1736/5533/files/cut-12-rosegold-bangle-crop-sm.png?v=1782372203","top":"65%","left":"50%","w":150,"op":0.56,"path":"lxDriftC","dur":"30s","delay":"0.5s","spin":"56s","blur":0,"core":false,"hideSm":true},{"src":"https://cdn.shopify.com/s/files/1/0999/1736/5533/files/cut-02-wave-ring-sm.png?v=1782369503","top":"72%","left":"80%","w":105,"op":0.58,"path":"lxDriftE","dur":"26s","delay":"2s","spin":"60s","blur":0,"core":false,"hideSm":true},{"src":"https://cdn.shopify.com/s/files/1/0999/1736/5533/files/cut-03-lightning-ring-sm.png?v=1782369512","top":"80%","left":"22%","w":100,"op":0.62,"path":"lxDriftF","dur":"34s","delay":"1.5s","spin":"64s","blur":0,"core":true,"hideSm":false},{"src":"https://cdn.shopify.com/s/files/1/0999/1736/5533/files/cut-08-infinity-bracelet-sm.png?v=1782369550","top":"87%","left":"62%","w":150,"op":0.54,"path":"lxDriftB","dur":"28s","delay":"2s","spin":"58s","blur":0,"core":false,"hideSm":false},{"src":"https://cdn.shopify.com/s/files/1/0999/1736/5533/files/cut-04-wirewrap-ring-sm.png?v=1782369519","top":"93%","left":"12%","w":120,"op":0.58,"path":"lxDriftD","dur":"36s","delay":"3s","spin":"70s","blur":0,"core":true,"hideSm":false}];
    var items = full ? all : all.filter(function (it) { return it.core; });
    var box = document.createElement('div');
    box.className = 'lx-floatbg' + (calm ? ' lx-floatbg--calm' : '');
    box.setAttribute('aria-hidden', 'true');
    items.forEach(function (it) {
      var d = document.createElement('div');
      d.className = 'lx-floatbg__item' + (it.hideSm ? ' lx-floatbg__item--hide-sm' : '');
      d.style.top = it.top;
      d.style.left = it.left;
      d.style.setProperty('--w', it.w + 'px');
      d.style.setProperty('--op', String(it.op));
      d.style.setProperty('--lxf-path', it.path);
      d.style.setProperty('--lxf-dur', it.dur);
      d.style.setProperty('--lxf-delay', it.delay);
      var img = document.createElement('img');
      img.className = 'lx-floatbg__img';
      img.src = it.src;
      img.alt = '';
      img.setAttribute('loading', 'lazy');
      img.setAttribute('decoding', 'async');
      img.setAttribute('aria-hidden', 'true');
      img.style.setProperty('--lxf-spin', it.spin);
      img.style.setProperty('--lxf-delay', it.delay);
      if (it.blur) img.style.filter = 'blur(' + it.blur + 'px)';
      d.appendChild(img);
      box.appendChild(d);
    });
    var cs = window.getComputedStyle(main);
    if (cs.position === 'static') main.style.position = 'relative';
    main.insertBefore(box, main.firstChild);
  }
  /*__LX_FLOATBG_END__*/

  /*__LX_PHASE15_START__*/
  /* 9. Social-proof strip — one slim branded line, placed contextually -------- */
  function initSocialProof() {
    if (document.querySelector('.lx-socialproof')) return;
    // Skip on product pages — the buybox already carries enough proof (rating,
    // social pills, guarantees), so this strip is redundant there.
    if (/\/products\//.test(window.location.pathname)) return;
    var strip = document.createElement('div');
    strip.className = 'lx-socialproof';
    strip.setAttribute('role', 'note');
    strip.innerHTML =
      '<span class="lx-socialproof__star" aria-hidden="true">\u2605</span>' +
      '<span>4.9/5 \u00b7 12,000+ happy customers</span>';

    var main = document.querySelector('main');
    if (!main) return;
    var first = main.querySelector(':scope > .shopify-section');
    // Keep the gift banner flush under the hero: anchor the social-proof strip
    // after the banner when it's present, otherwise right after the hero.
    var anchor = main.querySelector(':scope > .lx-gift-banner') || first;
    if (anchor) main.insertBefore(strip, anchor.nextSibling);
    else main.appendChild(strip);
  }

  /* 10. Branded footer block — wordmark + tagline + heritage line ------------- */
  function initFooterBrand() {
    var footer = document.querySelector('.footer');
    if (!footer || footer.querySelector('.lx-footbrand')) return;
    var box = document.createElement('div');
    box.className = 'lx-footbrand';
    box.innerHTML =
      '<span class="lx-footbrand__name">LUXINA</span>' +
      '<span class="lx-footbrand__tag">Timeless, handcrafted jewelry</span>' +
      '<span class="lx-footbrand__est">Est. 2014 \u00b7 Handcrafted Jewelry</span>';
    var bottom = footer.querySelector('.footer__content-bottom');
    if (bottom && bottom.parentNode) bottom.parentNode.insertBefore(box, bottom);
    else footer.appendChild(box);
  }
  /*__LX_PHASE15_END__*/

  /*__LX_PHASE18_START__*/
  /* 11. Facebook views badge — tuck it inline beside the quantity stepper
     (product pages only; idempotent). Styling/shrink handled in CSS. */
  function initFbBadge() {
    if (!/\/products\//.test(window.location.pathname)) return;
    // The social-proof pills (Facebook/TikTok/Pinterest) distract from the
    // Add-to-Cart CTA, so we keep them out of the buy box. CSS also hides them
    // for the no-JS case; this just makes sure nothing is relocated/animated.
    var badge = document.querySelector('.fb-badge-container');
    if (badge) badge.style.display = 'none';
  }
  /*__LX_PHASE18_END__*/

  /*__LX_PHASE20_START__*/
  /* 12. Product-page selling extras (product pages only; idempotent):
     - italic tagline under the product title
     - animated delivery truck prepended to the "Free Shipping" line
     - compact trust/benefits microlist below that line */
  function initProductExtras() {
    if (!/\/products\//.test(window.location.pathname)) return;
    var info = document.querySelector('.product__info-container');
    if (!info) return;

    // 12.1 — italic tagline directly under the product title group.
    var h1 = info.querySelector('.product__title h1');
    if (h1 && !info.querySelector('.lx-tagline')) {
      var titleBox = h1.closest('.product__title') || h1.parentNode;
      var tag = document.createElement('p');
      tag.className = 'lx-tagline';
      tag.textContent = '\u201C' + lxPickTagline() + '\u201D';
      titleBox.appendChild(tag);
    }

    // 12.2 — hide the shipping caption line, then render a short key-facts list
    // right under the price so the buy box leads with product benefits.
    var tax = info.querySelector('.product__tax');
    if (tax) tax.classList.add('lx-tax-hidden');

    if (!info.querySelector('.lx-benefits')) {
      var facts = [
        [LX_ICONS.drop, 'Waterproof & Tarnish-Free'],
        [LX_ICONS.heart, 'Hypoallergenic, gentle on skin'],
        [LX_ICONS.shield, 'Built to last, lifetime guarantee'],
      ];
      var ul = document.createElement('ul');
      ul.className = 'lx-benefits';
      ul.innerHTML = facts
        .map(function (b) {
          return (
            '<li class="lx-benefit-line"><span class="lx-benefit-ic" aria-hidden="true">' +
            b[0] +
            '</span><span>' +
            b[1] +
            '</span></li>'
          );
        })
        .join('');
      // Anchor after the shipping caption if present (it sits right below the
      // price), otherwise fall back to the price block.
      var anchor =
        tax ||
        info.querySelector('.price') ||
        info.querySelector('[id^="price-"]');
      if (anchor && anchor.parentNode) anchor.insertAdjacentElement('afterend', ul);
      else info.appendChild(ul);
    }
  }
  /*__LX_PHASE20_END__*/

  /*__LX_LAZYVIDEO_START__*/
  /* SPEED — lazy-load + viewport-gate every injected <video data-lx-lazyvideo>.
     Off-screen videos never download (markup sets preload="none" and drops the
     autoplay attribute); a single observer plays them when scrolled into view
     and pauses them when out of view. Visual behaviour is identical (muted,
     looping autoplay-on-screen) but the heavy UGC carousel + atelier .mov no
     longer download on initial load. Idempotent. */
  function initLazyVideos() {
    var vids = [].slice.call(document.querySelectorAll('video[data-lx-lazyvideo]'));
    if (!vids.length) return;
    function play(v) {
      try { if (v.preload !== 'auto') v.preload = 'auto'; } catch (e) {}
      var p = v.play();
      if (p && p.catch) p.catch(function () {});
    }
    if (!('IntersectionObserver' in window)) { vids.forEach(play); return; }
    var io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (en) {
          var v = en.target;
          if (en.isIntersecting) play(v);
          else { try { v.pause(); } catch (e) {} }
        });
      },
      { root: null, rootMargin: '200px 0px', threshold: 0.1 }
    );
    vids.forEach(function (v) {
      if (v.dataset.lxLazyReady) return;
      v.dataset.lxLazyReady = '1';
      io.observe(v);
    });
  }
  /*__LX_LAZYVIDEO_END__*/

  /*__LX_BUNDLEBOX_START__*/
  /* 13. Product-page "Buy 2, Get 1 Free" gift offer directly above Add-to-Cart +
     explicit savings/urgency next to the price. Cart-aware: build the set, then
     reveal the free-gift chooser (top-20 bestseller pool) once 2+ are in cart. */
  function lxDetectSep(text) {
    var t = String(text || '').replace(/[^0-9.,]/g, '');
    var hasDot = t.indexOf('.') > -1;
    var hasComma = t.indexOf(',') > -1;
    var dec = '.';
    if (hasDot && hasComma) dec = t.lastIndexOf('.') > t.lastIndexOf(',') ? '.' : ',';
    else if (hasComma && !hasDot) dec = t.split(',').pop().length === 2 ? ',' : '.';
    return dec;
  }
  function lxParsePrice(text) {
    if (text == null) return null;
    var t = String(text).replace(/[^0-9.,]/g, '').replace(/^[.,]+/, '').replace(/[.,]+$/, '');
    if (!t) return null;
    var lastSep = Math.max(t.lastIndexOf('.'), t.lastIndexOf(','));
    var n;
    if (lastSep === -1) {
      n = parseFloat(t);
    } else {
      var intPart = t.slice(0, lastSep).replace(/[.,]/g, '');
      var decPart = t.slice(lastSep + 1).replace(/[.,]/g, '');
      // 3+ trailing digits with a single separator => thousands grouping, no decimals.
      if (decPart.length >= 3 && t.indexOf('.') === t.lastIndexOf('.') && t.indexOf(',') === t.lastIndexOf(',')) {
        n = parseFloat((intPart + decPart) || '0');
      } else {
        n = parseFloat(intPart + '.' + decPart);
      }
    }
    return isFinite(n) ? n : null;
  }
  function lxFmtLikePrice(sample, value) {
    var dec = lxDetectSep(sample);
    var thou = dec === ',' ? '.' : ',';
    var s = dec === ',' ? value.toFixed(2).replace('.', ',') : value.toFixed(2);
    var parts = s.split(dec);
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thou);
    s = parts.join(dec);
    return String(sample).replace(/[0-9][0-9.,]*/, s).trim();
  }

  // Milestone stepper for the Buy-2-Get-1 offer: 1st piece -> 2nd piece ->
  // free gift. Shared markup + painter so the PDP box mirrors the cart drawer.
  var LX_STEP_GEM =
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"><path d="M6 3h12l4 6-10 12L2 9z"/><path d="M2 9h20"/><path d="M9 3 7 9l5 12 5-12-2-6"/></svg>';
  var LX_STEP_GIFT =
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="8" width="18" height="4" rx="1"/><path d="M5 12v9h14v-9"/><path d="M12 8v13"/><path d="M12 8S10.5 4 8.5 4A2.5 2.5 0 0 0 8.5 9H12z"/><path d="M12 8s1.5-4 3.5-4a2.5 2.5 0 0 1 0 5H12z"/></svg>';

  function lxStepsMarkup() {
    return (
      '<div class="lx-steps" data-lx-steps>' +
      '<div class="lx-steps__line"><i class="lx-steps__fill"></i></div>' +
      '<div class="lx-steps__nodes">' +
      '<div class="lx-steps__node" data-step="1"><span class="lx-steps__dot">' + LX_STEP_GEM + '</span><span class="lx-steps__cap">1st piece</span></div>' +
      '<div class="lx-steps__node" data-step="2"><span class="lx-steps__dot">' + LX_STEP_GEM + '</span><span class="lx-steps__cap">2nd piece</span></div>' +
      '<div class="lx-steps__node" data-step="3"><span class="lx-steps__dot">' + LX_STEP_GIFT + '</span><span class="lx-steps__cap">Free gift</span></div>' +
      '</div>' +
      '</div>'
    );
  }

  function lxStepsPaint(root, count) {
    if (!root) return;
    count = count || 0;
    var rem = count % 3;
    var fillPct = rem === 2 ? 100 : rem === 1 ? 50 : 0;
    var fill = root.querySelector('.lx-steps__fill');
    if (fill) fill.style.width = fillPct + '%';
    var nodes = root.querySelectorAll('.lx-steps__node');
    var done = [rem >= 1, rem === 2, rem === 2];
    var currentIdx = rem === 0 ? 0 : rem === 1 ? 1 : 2;
    for (var i = 0; i < nodes.length; i++) {
      nodes[i].classList.toggle('is-done', !!done[i]);
      nodes[i].classList.toggle('is-current', i === currentIdx);
    }
  }

  function initBundleBuybox() {
    if (!/\/products\//.test(window.location.pathname)) return;
    var info = document.querySelector('.product__info-container');
    if (!info || info.querySelector('.lx-gift-wrap')) return;
    var buttons = info.querySelector('.product-form__buttons');
    if (!buttons) return;
    var qtyInput = info.querySelector('[name="quantity"]');
    var priceEl = info.querySelector('.price');

    var curEl =
      priceEl &&
      (priceEl.querySelector('.price__regular .price-item--regular') ||
        priceEl.querySelector('.price-item--sale') ||
        priceEl.querySelector('.price-item--regular'));
    var cmpEl = priceEl && (priceEl.querySelector('.price__sale s') || priceEl.querySelector('s'));
    var curTxt = curEl ? curEl.textContent : '';
    var cmpTxt = cmpEl ? cmpEl.textContent : '';
    var unit = lxParsePrice(curTxt);
    var compare = lxParsePrice(cmpTxt);
    var sample = curTxt || cmpTxt || '';

    // Explicit savings + gentle urgency, right under the price.
    if (unit != null && compare != null && compare > unit && !info.querySelector('.lx-save')) {
      var save = compare - unit;
      var pct = Math.round((save / compare) * 100);
      var sv = document.createElement('div');
      sv.className = 'lx-save';
      sv.innerHTML =
        '<span class="lx-save__amt">You save ' + lxFmtLikePrice(sample, save) + ' (' + pct + '%)</span>' +
        '<span class="lx-save__urg">\uD83D\uDD25 Limited-time sale price</span>';
      priceEl.insertAdjacentElement('afterend', sv);
    }

    // "Buy 2, Get 1 Free" gift offer. Cart-aware: below the threshold we show
    // cross-sell pieces to build the set; once 2+ items are in the cart we reveal
    // the free-gift chooser (top-20 bestseller pool). The automatic BXGY discount
    // makes the cheapest eligible piece free at checkout.
    var pid =
      (window.ShopifyAnalytics &&
        ShopifyAnalytics.meta &&
        ShopifyAnalytics.meta.product &&
        ShopifyAnalytics.meta.product.id) ||
      (info.querySelector('[data-product-id]') &&
        info.querySelector('[data-product-id]').getAttribute('data-product-id'));
    if (!pid) return;

    var handle =
      (window.ShopifyAnalytics &&
        ShopifyAnalytics.meta &&
        ShopifyAnalytics.meta.product &&
        ShopifyAnalytics.meta.product.handle) ||
      (window.location.pathname.split('/products/')[1] || '').split(/[/?#]/)[0];
    if (!handle) return;

    var THRESHOLD = 2; // buy 2 pieces -> the 3rd (cheapest) is free

    var wrap = document.createElement('div');
    wrap.className = 'lx-gift-wrap';
    wrap.innerHTML =
      '<div class="lx-gift__head">' +
      '<span class="lx-gift__icon" aria-hidden="true">\uD83C\uDF81</span>' +
      '<span class="lx-gift__title">Buy 2, Get 1 <b>FREE</b></span>' +
      '<span class="lx-gift__badge lx-ends-today">Ends today</span>' +
      '</div>' +
      lxCountdownBlock('Ends tonight at midnight') +
      '<div class="lx-gift__progress">' +
      lxStepsMarkup() +
      '<div class="lx-gift__msg" aria-live="polite"></div>' +
      '</div>' +
      '<div class="lx-gift__slot"></div>' +
      '<div class="lx-gift__note">\u2713 Your cheapest piece is free \u2014 applied automatically at checkout. <strong>Offer ends tonight.</strong></div>';
    buttons.parentNode.insertBefore(wrap, buttons);

    var slot = wrap.querySelector('.lx-gift__slot');
    var steps = wrap.querySelector('[data-lx-steps]');
    var msg = wrap.querySelector('.lx-gift__msg');
    var cache = {}; // cached section nodes: build (cross-sell) and gift (chooser)
    var lastState = null;

    function setSlot(node) {
      slot.innerHTML = '';
      if (node) slot.appendChild(node.cloneNode(true));
    }

    function loadSection(key, sectionId, selector, onReady) {
      if (cache[key] !== undefined) {
        onReady(cache[key]);
        return;
      }
      fetch('/products/' + handle + '?section_id=' + sectionId, {
        headers: { Accept: 'text/html' },
        credentials: 'same-origin',
      })
        .then(function (r) {
          return r.ok ? r.text() : '';
        })
        .then(function (html) {
          var node = null;
          if (html) {
            var tmp = document.createElement('div');
            tmp.innerHTML = html;
            var el = tmp.querySelector(selector);
            if (el && el.querySelector('.lx-xsell__card')) node = el;
          }
          cache[key] = node;
          onReady(node);
        })
        .catch(function () {
          cache[key] = null;
          onReady(null);
        });
    }

    function render(count) {
      // Buy 2, Get 1 Free repeats -> every 3rd eligible piece is free.
      count = count || 0;
      var freebies = Math.floor(count / 3); // free pieces already secured
      var rem = count % 3;
      var toNext = rem === 2 ? 0 : rem === 1 ? 1 : 2; // pieces until the next free
      var claim = toNext === 0 && count >= 2; // a free piece is claimable now
      var hasfree = freebies >= 1;

      lxStepsPaint(steps, count);
      wrap.classList.toggle('is-unlocked', claim || hasfree);

      if (claim) {
        // The customer can pick a free piece right now -> show the gift chooser.
        msg.innerHTML = hasfree
          ? '<b>Another free piece unlocked.</b> Pick your next free piece:'
          : '<b>Free gift unlocked.</b> Pick your free piece:';
        if (lastState === 'gift') return;
        lastState = 'gift';
        loadSection('gift', 'lx-gift', '[data-lx-gift]', function (node) {
          if (lastState !== 'gift') return;
          setSlot(node);
        });
      } else {
        // Either still building the first set, or a gift is already secured and
        // we nudge toward the next one. Both show cross-sell pieces to add.
        if (hasfree) {
          msg.innerHTML =
            '\uD83C\uDF81 You\u2019ve already got <b>' + freebies + ' FREE</b>! Add <b>' +
            toNext + ' more piece' + (toNext > 1 ? 's' : '') +
            '</b> and your next one is <b>FREE</b> too.';
        } else {
          var need = 2 - count;
          msg.innerHTML =
            'Add <b>' + need + ' more piece' + (need > 1 ? 's' : '') +
            '</b> and your 3rd is <b>FREE</b> \uD83C\uDF81';
        }
        if (lastState === 'build') return;
        lastState = 'build';
        loadSection('build', 'lx-quickadd', '[data-lx-xsell]', function (node) {
          if (lastState !== 'build') return;
          setSlot(node);
        });
      }
    }

    function lxNotifyCart() {
      lxBroadcastCartChange({});
    }

    function refresh() {
      return fetch('/cart.js', { headers: { Accept: 'application/json' }, credentials: 'same-origin' })
        .then(function (r) {
          return r.json();
        })
        .then(function (c) {
          render(c.item_count || 0);
        })
        .catch(function () {
          render(0);
        });
    }

    slot.addEventListener('click', function (e) {
      var btn = e.target.closest('.lx-xsell__add');
      if (!btn || btn.disabled) return;
      var vid = btn.getAttribute('data-vid');
      if (!vid) return;
      var orig = btn.innerHTML;
      btn.disabled = true;
      btn.textContent = 'Adding\u2026';
      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ items: [{ id: parseInt(vid, 10), quantity: 1 }] }),
      })
        .then(function (r) {
          if (!r.ok) throw new Error('add failed');
          return r.json();
        })
        .then(function () {
          btn.innerHTML = '\u2713 Added';
          btn.classList.add('is-added');
          lxNotifyCart();
          return refresh();
        })
        .catch(function () {
          btn.disabled = false;
          btn.innerHTML = orig;
        });
    });

    // React to the main Add-to-Cart button and app cart events so the gift
    // unlocks live without a page reload.
    var mainAtc = buttons.querySelector('[name="add"], button[type="submit"], .product-form__submit');
    if (mainAtc) {
      mainAtc.addEventListener('click', function () {
        setTimeout(refresh, 1400);
      });
    }
    ['cart:refresh', 'cart:build', 'ajaxProduct:added', 'cart:updated'].forEach(function (ev) {
      document.addEventListener(ev, function () {
        setTimeout(refresh, 300);
      });
    });

    refresh();
  }
  /*__LX_BUNDLEBOX_END__*/

  /*__LX_CARTDRAWER_START__*/
  /* 14. Native cart drawer enhancements (replaces the UpCart slide-cart).
     The reward progress bar + upsell list are rendered server-side inside
     snippets/cart-drawer.liquid, so Dawn keeps them in sync on every cart
     change automatically. Here we only wire the upsell "+ Add" buttons: add
     via the Ajax API (requesting the cart-drawer section) and swap the drawer
     markup in place, then refresh the cart count. Idempotent + defensive. */

  /* Hide empty Dawn cart error icons (red !) that leak under remove buttons. */
  function scrubCartItemErrors(root) {
    var scope = root || document;
    var nodes = scope.querySelectorAll('.cart-item__error');
    for (var i = 0; i < nodes.length; i++) {
      var box = nodes[i];
      var text = box.querySelector('.cart-item__error-text');
      var has = text && String(text.textContent || '').trim().length > 0;
      if (!has) {
        if (text) text.textContent = '';
        box.setAttribute('hidden', '');
        box.style.display = 'none';
      } else {
        box.removeAttribute('hidden');
        box.style.display = '';
      }
    }
  }

  function initCartErrorScrub() {
    if (document.documentElement.__lxCartErrScrub) return;
    document.documentElement.__lxCartErrScrub = true;
    scrubCartItemErrors(document);
    if ('MutationObserver' in window) {
      new MutationObserver(function () {
        scrubCartItemErrors(document);
      }).observe(document.body, { childList: true, subtree: true, characterData: true });
    }
  }

  /* Mobile: keep express wallets collapsed so cart items stay visible.
     Desktop: force <details> open so Shop Pay / Apple Pay stay one tap away.
     Do NOT re-close after the shopper expands it (no MutationObserver loop). */
  function syncCartExpressDetails() {
    var mq = window.matchMedia && window.matchMedia('(max-width: 749px)');
    var mobile = mq && mq.matches;
    var nodes = document.querySelectorAll('#CartDrawer .lx-cd-express-details');
    for (var i = 0; i < nodes.length; i++) {
      var el = nodes[i];
      if (mobile) {
        if (!el.__lxExpressReady) {
          el.removeAttribute('open');
          el.__lxExpressReady = true;
        }
      } else {
        el.setAttribute('open', '');
        el.__lxExpressReady = true;
      }
    }
  }
  function initCartExpressDetails() {
    if (document.documentElement.__lxExpressInit) return;
    document.documentElement.__lxExpressInit = true;
    syncCartExpressDetails();
    var mq = window.matchMedia && window.matchMedia('(max-width: 749px)');
    if (mq) {
      var onChange = function () {
        var nodes = document.querySelectorAll('#CartDrawer .lx-cd-express-details');
        for (var i = 0; i < nodes.length; i++) nodes[i].__lxExpressReady = false;
        syncCartExpressDetails();
      };
      if (mq.addEventListener) mq.addEventListener('change', onChange);
      else if (mq.addListener) mq.addListener(onChange);
    }
  }

  function initCartDrawer() {
    var root = document.querySelector('cart-drawer');
    if (!root || root.__lxCdBound) return;
    root.__lxCdBound = true;

    function extractInner(html, selector) {
      var tmp = document.createElement('div');
      tmp.innerHTML = html;
      var el = tmp.querySelector(selector);
      return el ? el.innerHTML : null;
    }

    function renderDrawer(sections) {
      if (!sections) return;
      var drawerHtml = sections['cart-drawer'];
      if (drawerHtml) {
        var cd = document.getElementById('CartDrawer');
        var inner = extractInner(drawerHtml, '#CartDrawer');
        if (cd && inner != null) {
          cd.innerHTML = inner;
          root.classList.remove('is-empty');
          try { syncCartExpressDetails(); } catch (e) {}
          try { scrubCartItemErrors(cd); } catch (e) {}
        }
      }
    }

    function refreshBubble() {
      fetch('/cart.js', {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
        cache: 'no-store',
      })
        .then(function (r) { return r.json(); })
        .then(function (cart) {
          var count = cart && cart.item_count;
          document.querySelectorAll('.cart-count-bubble').forEach(function (b) {
            var span = b.querySelector('span[aria-hidden="true"]') || b.querySelector('span');
            if (span) span.textContent = count;
          });
          ['cart:refresh', 'cart:updated', 'ajaxProduct:added'].forEach(function (ev) {
            try {
              document.dispatchEvent(new CustomEvent(ev, { bubbles: true, detail: { cart: cart } }));
            } catch (e) {}
          });
        })
        .catch(function () {});
    }

    root.addEventListener('click', function (e) {
      var btn = e.target.closest('.lx-cd-up__add');
      if (!btn || btn.disabled) return;
      e.preventDefault();
      var vid = btn.getAttribute('data-vid');
      if (!vid) return;
      var orig = btn.innerHTML;
      btn.disabled = true;
      btn.textContent = 'Adding\u2026';
      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({
          items: [{ id: parseInt(vid, 10), quantity: 1 }],
          sections: ['cart-drawer'],
          sections_url: window.location.pathname,
        }),
      })
        .then(function (r) {
          if (!r.ok) throw new Error('add failed');
          return r.json();
        })
        .then(function (data) {
          renderDrawer(data.sections);
          refreshBubble();
          // Full sync (offers, cart page, header bubble, pub/sub) is triggered
          // automatically by the global cart fetch hook (lxInitCartSync) that
          // wraps this /cart/add.js call, so we don't re-render the drawer again
          // here — that double render was part of the post-add "spinning" churn.
        })
        .catch(function () {
          btn.disabled = false;
          btn.innerHTML = orig;
        });
    });
  }
  /*__LX_CARTDRAWER_END__*/

  /* Cart PAGE: show the live order total inside the checkout button (the button
     sits outside Dawn's re-rendered .js-contents, so we mirror the value from
     .totals__total-value and keep it in sync on every quantity change). */
  function initCartPageTotal() {
    var footer = document.querySelector('#main-cart-footer');
    if (!footer || footer.__lxCcBound) return;
    footer.__lxCcBound = true;

    function totalText() {
      var t = footer.querySelector('.totals__total-value');
      return t ? t.textContent.trim() : '';
    }

    function render() {
      var btn = footer.querySelector('#checkout, .cart__checkout-button');
      if (!btn) return;
      var total = totalText();
      if (!total) return;
      if (!btn.querySelector('.lx-cc__total') && !btn.querySelector('.lx-cd-co__total')) {
        btn.classList.add('lx-cc', 'lx-cd-co');
        btn.innerHTML =
          '<svg class="lx-cd-co__lock" viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="4.5" y="10.5" width="15" height="10" rx="2"/><path d="M8 10.5V7a4 4 0 0 1 8 0v3.5"/></svg>' +
          '<span class="lx-cc__label lx-cd-co__label">Checkout Now</span>' +
          '<span class="lx-cc__dot lx-cd-co__dot" aria-hidden="true"></span>' +
          '<span class="lx-cc__total lx-cd-co__total"></span>';
      }
      var span = btn.querySelector('.lx-cd-co__total, .lx-cc__total');
      if (span && span.textContent !== total) span.textContent = total;
    }

    render();
    var obs = new MutationObserver(function () {
      render();
    });
    obs.observe(footer, { childList: true, subtree: true, characterData: true });
  }

  /* Scroll memory for browsing: when a shopper clicks a product from a listing
     (collection / search / bestseller) and then hits back, return them to the
     exact card they clicked instead of the top of the page. Anchors to the
     clicked card element so it stays correct even if lazy images shift layout.
     Works with or without bfcache. */
  function initScrollMemory() {
    var path = location.pathname;
    var isListing =
      /\/collections\//.test(path) ||
      /\/search/.test(path) ||
      !!document.querySelector('#product-grid, ul.product-grid, .product-grid');
    var KEY = 'lx-scroll:' + path + location.search;

    // Save the clicked product + scroll offset the moment a product link is used.
    document.addEventListener(
      'click',
      function (e) {
        var a = e.target.closest && e.target.closest('a[href*="/products/"]');
        if (!a) return;
        try {
          var handle = (a.getAttribute('href') || '').split('/products/')[1] || '';
          handle = handle.split(/[?#]/)[0];
          sessionStorage.setItem(
            KEY,
            JSON.stringify({ y: window.scrollY || window.pageYOffset || 0, handle: handle, t: Date.now() })
          );
        } catch (_) {}
      },
      true
    );

    if (!isListing) return;

    // Manual restoration on this listing so we control the position precisely.
    try {
      if ('scrollRestoration' in history) history.scrollRestoration = 'manual';
    } catch (_) {}

    // Keep the saved offset fresh while browsing the listing (throttled).
    var saveT = null;
    window.addEventListener(
      'scroll',
      function () {
        if (saveT) return;
        saveT = setTimeout(function () {
          saveT = null;
          try {
            var raw = sessionStorage.getItem(KEY);
            var obj = raw ? JSON.parse(raw) : {};
            obj.y = window.scrollY || window.pageYOffset || 0;
            sessionStorage.setItem(KEY, JSON.stringify(obj));
          } catch (_) {}
        }, 250);
      },
      { passive: true }
    );

    function doRestore() {
      var raw;
      try {
        raw = sessionStorage.getItem(KEY);
      } catch (_) {
        return;
      }
      if (!raw) return;
      var data;
      try {
        data = JSON.parse(raw);
      } catch (_) {
        return;
      }
      var target = null;
      if (data.handle) {
        var link = document.querySelector('a[href*="/products/' + data.handle + '"]');
        if (link) target = (link.closest && link.closest('.grid__item, li, .card-wrapper')) || link;
      }
      // Stop re-aligning the moment the shopper takes over scrolling, so we
      // never fight their input.
      var interrupted = false;
      var stop = function () {
        interrupted = true;
      };
      ['wheel', 'touchstart', 'keydown', 'pointerdown'].forEach(function (ev) {
        window.addEventListener(ev, stop, { passive: true, once: true });
      });
      var apply = function () {
        if (interrupted) return;
        var y;
        if (target) {
          var rect = target.getBoundingClientRect();
          // Land the clicked card roughly a third down the viewport — close to
          // where it sat when clicked, and robust to layout shifts.
          y = (window.scrollY || window.pageYOffset || 0) + rect.top - Math.max(0, window.innerHeight * 0.28);
        } else if (typeof data.y === 'number') {
          y = data.y;
        } else {
          return;
        }
        window.scrollTo(0, Math.max(0, Math.round(y)));
      };
      apply();
      requestAnimationFrame(apply);
      setTimeout(apply, 150);
      setTimeout(apply, 400);
      setTimeout(apply, 800);
      // Re-align once late/lazy images finish and the layout settles.
      window.addEventListener('load', function () {
        setTimeout(apply, 60);
      });
    }

    function shouldRestore(persisted) {
      if (persisted) return true;
      var nav = (performance.getEntriesByType && performance.getEntriesByType('navigation')[0]) || {};
      if (nav.type === 'back_forward') return true;
      // Fallback: arrived here straight from a product page on the same origin.
      try {
        if (document.referrer && /\/products\//.test(document.referrer)) {
          var r = new URL(document.referrer);
          if (r.origin === location.origin) return true;
        }
      } catch (_) {}
      return false;
    }

    window.addEventListener('pageshow', function (ev) {
      if (shouldRestore(ev && ev.persisted)) doRestore();
    });
    // pageshow may have already fired before this script ran on a fresh load.
    if (document.readyState === 'complete' && shouldRestore(false)) doRestore();
  }

  /* Ring Size Guide: on ring product pages only, add a subtle "Ring Size Guide"
     trigger next to the Size variant label that opens an accessible modal with a
     ring-size conversion table. Ring detection comes from the #lx-ring-meta flag
     printed by layout/theme.liquid. Fully additive + guarded against re-runs. */
  function initRingSizeGuide() {
    if (!/\/products\//.test(window.location.pathname)) return;
    var meta = document.getElementById('lx-ring-meta');
    if (!meta) return;
    var isRing = false;
    try {
      isRing = !!JSON.parse(meta.textContent || '{}').isRing;
    } catch (e) {}
    if (!isRing) return;

    var scope =
      document.querySelector('.product__info-container') || document.querySelector('main') || document;
    var labels = scope.querySelectorAll(
      '.product-form__input legend.form__label, .product-form__input label.form__label'
    );
    var target = null;
    for (var i = 0; i < labels.length; i++) {
      var t = (labels[i].textContent || '').trim().toLowerCase();
      if (
        t.indexOf('size') !== -1 ||
        t.indexOf('gr\u00f6\u00dfe') !== -1 || // größe
        t.indexOf('gr\u00f6sse') !== -1 || // grösse
        t.indexOf('grosse') !== -1 ||
        t.indexOf('ringgr') !== -1
      ) {
        target = labels[i];
        break;
      }
    }
    if (!target) return;
    // Guard against duplicates / re-runs.
    if (scope.querySelector('.lx-rsg-trigger')) return;

    var trigger = document.createElement('button');
    trigger.type = 'button';
    trigger.className = 'lx-rsg-trigger';
    trigger.textContent = 'Ring Size Guide';
    trigger.setAttribute('aria-haspopup', 'dialog');
    target.appendChild(document.createTextNode(' '));
    target.appendChild(trigger);

    var rows = [
      ['ca. 16,5 mm', '52', '6'],
      ['ca. 17,2 mm', '54', '7'],
      ['ca. 17,8 mm', '56', '8'],
      ['ca. 18,5 mm', '58', '9'],
      ['ca. 19,1 mm', '60', '10'],
      ['ca. 19,8 mm', '62', '11'],
      ['ca. 20,4 mm', '64', '12'],
    ];
    var bodyRows = rows
      .map(function (r) {
        return '<tr><td>' + r[0] + '</td><td>' + r[1] + '</td><td>' + r[2] + '</td></tr>';
      })
      .join('');

    var overlay = null;
    var lastFocus = null;

    function onKeydown(ev) {
      if (ev.key === 'Escape' || ev.keyCode === 27) {
        closeModal();
        return;
      }
      if (ev.key === 'Tab' && overlay) {
        // Simple focus trap within the dialog.
        var focusable = overlay.querySelectorAll(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );
        if (!focusable.length) return;
        var first = focusable[0];
        var last = focusable[focusable.length - 1];
        if (ev.shiftKey && document.activeElement === first) {
          ev.preventDefault();
          last.focus();
        } else if (!ev.shiftKey && document.activeElement === last) {
          ev.preventDefault();
          first.focus();
        }
      }
    }

    function closeModal() {
      if (!overlay) return;
      overlay.setAttribute('hidden', '');
      document.documentElement.classList.remove('lx-rsg-open');
      document.removeEventListener('keydown', onKeydown, true);
      if (lastFocus && lastFocus.focus) lastFocus.focus();
    }

    function openModal() {
      if (!overlay) {
        overlay = document.createElement('div');
        overlay.className = 'lx-rsg-overlay';
        overlay.setAttribute('hidden', '');
        overlay.innerHTML =
          '<div class="lx-rsg-modal" role="dialog" aria-modal="true" aria-label="Ring Size Guide">' +
          '<button type="button" class="lx-rsg-close" aria-label="Close">\u00d7</button>' +
          '<h2 class="lx-rsg-title">Ring Size Guide</h2>' +
          '<div class="lx-rsg-tablewrap">' +
          '<table class="lx-rsg-table">' +
          '<thead><tr><th>Innendurchmesser</th><th>EU-Gr\u00f6\u00dfe (Umfang)</th><th>US-Gr\u00f6\u00dfe</th></tr></thead>' +
          '<tbody>' +
          bodyRows +
          '</tbody>' +
          '</table>' +
          '</div>' +
          '</div>';
        document.body.appendChild(overlay);
        overlay.addEventListener('click', function (ev) {
          if (ev.target === overlay) closeModal();
        });
        overlay.querySelector('.lx-rsg-close').addEventListener('click', closeModal);
      }
      lastFocus = document.activeElement;
      overlay.removeAttribute('hidden');
      document.documentElement.classList.add('lx-rsg-open');
      document.addEventListener('keydown', onKeydown, true);
      var closeBtn = overlay.querySelector('.lx-rsg-close');
      if (closeBtn && closeBtn.focus) closeBtn.focus();
    }

    trigger.addEventListener('click', function (ev) {
      ev.preventDefault();
      openModal();
    });
  }

  /*__LX_SOLDPILL_START__*/
  /* Premium social-proof "sold" pill on the PDP rating line (product pages
     only; idempotent; purely presentational). Mirrors the "10,000+ customers"
     benefit already shown on the page, on one tidy line next to the rating. */
  function initSoldPill() {
    if (!/\/products\//.test(window.location.pathname)) return;
    var info = document.querySelector('.product__info-container');
    if (!info) return;
    var badge = info.querySelector('.custom-review-badge');
    if (!badge || badge.querySelector('.lx-soldpill')) return;
    var pill = document.createElement('span');
    pill.className = 'lx-soldpill';
    pill.setAttribute('aria-hidden', 'false');
    pill.textContent = '10,000+ sold';
    badge.appendChild(pill);
  }
  /*__LX_SOLDPILL_END__*/

  function init() {
    lxInitCartSync();
    initOfferCountdown();
    var steps = [
      initHeaderElevation,
      initReveal,
      initStickyAtc,
      initUspBar,
      initGiftHomeBanner,
      initTidyHeader,
      initBuyboxEnhancements,
      initPdpExpressRow,
      initBundleBuybox /*__LX_BUNDLEBOX_CALL__*/,
      initCartErrorScrub,
      initCartExpressDetails,
      initCartDrawer /*__LX_CARTDRAWER_CALL__*/,
      initCartPageTotal,
      initUpsellSlider,
      initOffers,
      initFloatBg /*__LX_FLOATBG_CALL__*/,
      initFooterBrand /*__LX_PHASE15_CALL__*/,
      initFbBadge /*__LX_PHASE18_CALL__*/,
      initProductExtras /*__LX_PHASE20_CALL__*/,
      initSoldPill /*__LX_SOLDPILL_CALL__*/,
      initRingSizeGuide,
      initLazyVideos /*__LX_LAZYVIDEO_CALL__*/,
      initScrollMemory,
      initSnapTracking,
    ];
    for (var i = 0; i < steps.length; i++) {
      try {
        steps[i]();
      } catch (e) {
        if (window.console && console.warn) console.warn('[lx] init step failed:', e);
      }
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
